import { Button, Group, Stack, Text, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useRef, useState } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { useMutation } from "urql";
import {
    suggestImprovementMutation,
    type TuneThreadAiSuggestImprovementMutation,
    type TuneThreadAiSuggestImprovementMutationVariables,
} from "./queries";
import type { ThreadSuggestionData } from "./types";

interface ImprovementModalProps {
    hasAnyJudgements: boolean;
    currentInstructions: string;
    threads: ThreadSuggestionData[];
    onApplied: (newPrompt: string) => void;
    onClose: () => void;
}

function ImprovementModal({
    hasAnyJudgements,
    currentInstructions,
    threads,
    onApplied,
    onClose,
}: ImprovementModalProps): JSX.Element {
    const noteRef = useRef<HTMLTextAreaElement | null>(null);
    const [result, execute] = useMutation<
        TuneThreadAiSuggestImprovementMutation,
        TuneThreadAiSuggestImprovementMutationVariables
    >(suggestImprovementMutation);

    const [suggestedPrompt, setSuggestedPrompt] = useState<string | null>(null);

    const denseDiffStyles = {
        diffContainer: { fontSize: "12px", pre: { lineHeight: 1.25 } },
        contentText: { fontSize: "12px" },
        line: { padding: "2px 6px" },
        gutter: { padding: "2px 6px", minWidth: 28 },
        wordDiff: { padding: "0 2px", borderRadius: 2 },
    } as const;

    const generateImprovement = async () => {
        const note = noteRef.current?.value ?? "";
        if (!hasAnyJudgements && note.trim() === "") {
            notifications.show({
                title: "Notes required",
                message: "Please provide notes since there are no LLM judgements.",
            });
            return;
        }

        const exec = await execute({
            currentInstructions,
            manualNote: note.trim() || null,
            threads: threads.map((t) => ({
                tuneThreadId: t.tuneThreadId,
                answer: t.answer,
                llmJudgeScore: t.llmJudgeScore ?? null,
                llmJudgeExplanation: t.llmJudgeExplanation ?? null,
            })),
        });

        if (exec.error) {
            notifications.show({
                title: "Failed to generate improvement",
                message: exec.error.message,
            });
            return;
        }

        const newPrompt = exec.data!.tuneThreadAiSuggestImprovement;
        setSuggestedPrompt(newPrompt);
    };

    const applyImprovement = () => {
        if (!suggestedPrompt) return;
        onApplied(suggestedPrompt);
        notifications.show({ title: "Improved prompt applied", message: "System prompt updated" });
        onClose();
    };

    return (
        <Stack>
            {suggestedPrompt ? (
                <Stack>
                    <Text c="dimmed" size="sm">
                        Review the changes before applying. You can edit notes and regenerate if needed.
                    </Text>
                    <ReactDiffViewer
                        oldValue={currentInstructions}
                        newValue={suggestedPrompt}
                        leftTitle="Current prompt"
                        rightTitle="Suggested prompt"
                        splitView
                        hideLineNumbers
                        compareMethod={DiffMethod.WORDS}
                        showDiffOnly={false}
                        styles={denseDiffStyles}
                    />
                </Stack>
            ) : null}

            <Textarea
                label="Manual improvement request"
                description={hasAnyJudgements ? "Optional" : "Required when there are no LLM judgements"}
                autosize
                minRows={3}
                ref={noteRef}
                required={!hasAnyJudgements}
            />

            <Group justify="space-between">
                <Button
                    loading={result.fetching}
                    onClick={generateImprovement}
                    disabled={result.fetching}
                    variant={suggestedPrompt ? "light" : "filled"}
                >
                    {suggestedPrompt ? "Regenerate" : "Generate improvement"}
                </Button>
                <Button onClick={applyImprovement} disabled={!suggestedPrompt}>
                    Apply
                </Button>
            </Group>
        </Stack>
    );
}

export default ImprovementModal;
